import { OrgConfigService } from '../services/orgConfigService';

/**
 * The public-holiday calendar.
 *
 * ── It is data now, not a constant ────────────────────────────────────────────────────────
 *
 * These dates used to be the whole story: a hardcoded array, so publishing 2027 meant a code
 * change and a deploy, HR could not do it at all, and every consumer went empty after the last
 * date of the year it happened to cover.
 *
 * The list now lives in OrgConfig — the same server-backed store as Teams, Job Titles and
 * Employment Types — and HR maintains it in Settings → Organisation Setup. The array below is
 * what a fresh install starts with and the fallback whenever the server has nothing, so
 * behaviour before the first edit is exactly what it always was.
 *
 * ── Every function below kept its signature ───────────────────────────────────────────────
 *
 * Eight modules import from here — Apply Leave, the Leave Dashboard, Shift Roster, Home, the
 * attendance orchestrator, leave duration and the shift report. None of them changed. Reads stay
 * SYNCHRONOUS, resolved from OrgConfigService's localStorage cache, because they are called
 * while rendering a calendar cell and inside a pure attendance calculation — neither can await.
 * OrgConfigService.refresh() fills that cache; the setters write through it.
 */

export interface Holiday {
  date: string;
  name: string;
}

/** What a fresh install starts with. The list these dates replaced, unchanged. */
export const HOLIDAYS_2026: Holiday[] = [
  { date: '2026-01-01', name: "New Year's Day" },
  { date: '2026-01-15', name: 'Makar Sankranti' },
  { date: '2026-01-26', name: 'Republic Day' },
  { date: '2026-03-04', name: 'Holi' },
  { date: '2026-03-20', name: 'Ramzan Id / Eid-ul-Fitr' },
  { date: '2026-05-01', name: 'May Day' },
  { date: '2026-09-14', name: 'Ganesh Chaturthi' },
  { date: '2026-10-02', name: 'Gandhi Jayanti' },
  { date: '2026-10-20', name: 'Dussehra' },
  { date: '2026-11-09', name: 'Deepavali' },
  { date: '2026-12-25', name: 'Christmas' },
];

/**
 * Published holiday calendars, keyed by year.
 *
 * Add a new year by dropping its list in here — every consumer reads through
 * this map, so nothing else needs touching. A year with no entry yields an
 * empty list rather than falling back to another year's dates, which would
 * silently show the wrong holidays.
 */
export const HOLIDAYS_BY_YEAR: Record<number, Holiday[]> = {
  2026: HOLIDAYS_2026,
};

/** The shipped calendar as one flat list — the fallback handed to OrgConfigService. */
export const DEFAULT_HOLIDAYS: Holiday[] = Object.values(HOLIDAYS_BY_YEAR)
  .flat()
  .slice()
  .sort((a, b) => a.date.localeCompare(b.date));

/**
 * Every published holiday, whatever the year, earliest first.
 *
 * One flat list rather than the year map it replaced: HR publishes dates, not years, and a store
 * keyed by year would have to be migrated every January. The year filtering the callers want is
 * done by getHolidaysForYear below, which is where it always belonged.
 */
export const getAllHolidays = (): Holiday[] => OrgConfigService.getHolidays(DEFAULT_HOLIDAYS);

/** Every holiday published for `year`, earliest first. Empty when not yet published. */
export const getHolidaysForYear = (year: number): Holiday[] =>
  getAllHolidays().filter(h => Number(h.date.slice(0, 4)) === year);

export const getHolidayForDate = (dateStr: string): Holiday | undefined => {
  // Matched against the whole published list, not a single year's slice: the previous version
  // could never match a date in any other year, so a 2027 public holiday would have rendered as
  // an ordinary working day on the roster and calendars.
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dateStr || ''))) return undefined;
  return getAllHolidays().find(h => h.date === dateStr);
};

/** Local calendar date as YYYY-MM-DD. Avoids the UTC shift toISOString() would apply. */
const toDateKey = (date: Date): string => {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

/**
 * The next `count` holidays on or after `from`, earliest first.
 *
 * Compares YYYY-MM-DD strings rather than Date objects so a holiday is "upcoming"
 * for the whole of its own day regardless of the viewer's timezone.
 *
 * Returns fewer than `count` (or none) once the calendar runs out — HOLIDAYS_2026
 * only covers 2026, so this empties after 25 Dec 2026 until the next year's dates
 * are added. Callers must handle a short or empty list.
 */
export const getUpcomingHolidays = (count = 2, from: Date = new Date()): Holiday[] => {
  const todayKey = toDateKey(from);
  return getHolidaysForYear(from.getFullYear())
    .filter(h => h.date >= todayKey)
    .slice(0, count);
};

/**
 * Holidays to show for a specific selected year.
 *
 * For the current year that means "still to come"; for a future year the whole
 * published calendar, since nothing in it has passed. Without the year split, a
 * viewer switching to 2027 would keep seeing 2026's remaining dates.
 */
export const getHolidaysForSelectedYear = (
  year: number,
  count = Infinity,
  from: Date = new Date()
): Holiday[] => {
  const list = getHolidaysForYear(year);
  const cutoff = year === from.getFullYear() ? toDateKey(from) : `${year}-01-01`;
  return list.filter(h => h.date >= cutoff).slice(0, count === Infinity ? undefined : count);
};

/** Whole days from `from` until the holiday. 0 means it is today. */
export const daysUntilHoliday = (holiday: Holiday, from: Date = new Date()): number => {
  const [year, month, day] = holiday.date.split('-').map(Number);
  const target = new Date(year, month - 1, day);
  const start = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  return Math.round((target.getTime() - start.getTime()) / 86400000);
};

